import { askConfirm } from '@/components/ui/ConfirmHost';
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getQuestionBanks, postQuestionBanks,
  getQuestionBanksQuestions, postQuestionBanksQuestions, putQuestions, deleteQuestions,
  postQuestionBanksImport,
} from '@/api/teacher';
import { getSubjects, getClassLevels } from '@/api/owner';
import {
  Plus, X, Edit2, Trash2, FileText, Upload, AlertCircle, BookOpen,
  ChevronRight, Check, ArrowLeft, Save, Clipboard, Eye,
} from 'lucide-react';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  if (Array.isArray(src.items)) return src.items;
  return [];
};

const unwrap = (r: any) => r?.data?.data ?? r?.data ?? r;

// ═══════════════════════════════════════════════════════════════
//  TYPES
// ═══════════════════════════════════════════════════════════════
type QType = 'mcq' | 'multi' | 'true_false' | 'short' | 'essay' | 'fill_blank';
type Difficulty = 'easy' | 'medium' | 'hard';

interface QuestionOption {
  body: string;
  is_correct: boolean;
}

interface QuestionForm {
  id?: number;
  type: QType;
  body: string;
  marks: string;
  difficulty: Difficulty;
  explanation: string;
  options: QuestionOption[];
}

const emptyQuestion = (): QuestionForm => ({
  type: 'mcq',
  body: '',
  marks: '1',
  difficulty: 'easy',
  explanation: '',
  options: [
    { body: '', is_correct: true },
    { body: '', is_correct: false },
    { body: '', is_correct: false },
    { body: '', is_correct: false },
  ],
});

const needsOptions = (t: QType) => ['mcq', 'multi', 'true_false'].includes(t);

// ═══════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export function QuestionBanksPage() {
  const qc = useQueryClient();
  const [openBank, setOpenBank] = useState<any | null>(null);
  const [showNewBank, setShowNewBank] = useState(false);
  const [questionForm, setQuestionForm] = useState<QuestionForm | null>(null);
  const [showImport, setShowImport] = useState(false);

  // ── queries ──
  const banksQ = useQuery({
    queryKey: ['question-banks'],
    queryFn: () => getQuestionBanks().then(unwrap),
  });
  const subjectsQ = useQuery({
    queryKey: ['owner-subjects'],
    queryFn: () => getSubjects({ per_page: 500 }).then(unwrap),
  });
  const levelsQ = useQuery({
    queryKey: ['owner-class-levels'],
    queryFn: () => getClassLevels().then(unwrap),
  });
  const questionsQ = useQuery({
    queryKey: ['question-bank-questions', openBank?.id],
    queryFn: () => getQuestionBanksQuestions(openBank!.id).then(unwrap),
    enabled: !!openBank?.id,
  });

  const banks = arr<any>(banksQ.data);
  const subjects = arr<any>(subjectsQ.data);
  const levels = arr<any>(levelsQ.data);
  const questions = arr<any>(questionsQ.data);

  const subjectMap = useMemo(() => {
    const m: Record<number, string> = {};
    subjects.forEach((s: any) => { m[s.id] = s.name; });
    return m;
  }, [subjects]);

  const levelMap = useMemo(() => {
    const m: Record<number, string> = {};
    levels.forEach((l: any) => { m[l.id] = l.name; });
    return m;
  }, [levels]);

  // ── mutations ──
  const createBankM = useMutation({
    mutationFn: (body: any) => postQuestionBanks(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['question-banks'] });
      setShowNewBank(false);
    },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Create failed'),
  });

  const deleteQ = useMutation({
    mutationFn: (id: number) => deleteQuestions(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['question-bank-questions', openBank?.id] });
    },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Delete failed'),
  });

  // ═══════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Teaching tools</p>
          <h1 className="text-3xl font-extrabold text-slate-900">
            {openBank ? `Questions · ${openBank.name}` : 'Question Banks'}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {openBank
              ? `${questions.length} question(s) in this bank`
              : 'Reusable collections of questions that feed CBT exams'}
          </p>
        </div>
        <div className="flex gap-2">
          {openBank ? (
            <>
              <button onClick={() => setOpenBank(null)}
                className="px-3 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> All banks
              </button>
              <button onClick={() => setShowImport(true)}
                className="px-3 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1">
                <Upload className="w-4 h-4" /> Import
              </button>
              <button onClick={() => setQuestionForm(emptyQuestion())}
                className="px-3 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-1">
                <Plus className="w-4 h-4" /> Add question
              </button>
            </>
          ) : (
            <button onClick={() => setShowNewBank(true)}
              className="px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-1">
              <Plus className="w-4 h-4" /> New bank
            </button>
          )}
        </div>
      </div>

      {!openBank && (
        <>
          {banksQ.isLoading ? (
            <p className="text-sm text-slate-500 py-8 text-center">Loading…</p>
          ) : banks.length === 0 ? (
            <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
              <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="font-semibold text-slate-700">No question banks yet</p>
              <p className="text-sm text-slate-500 mt-1">Create your first bank to start adding questions.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {banks.map((b: any) => (
                <button key={b.id} onClick={() => setOpenBank(b)}
                  className="text-left bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:border-slate-400 transition">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-bold text-slate-900 truncate">{b.name}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {b.subject_id && subjectMap[b.subject_id] ? subjectMap[b.subject_id] : 'No subject'}
                        {' · '}
                        {b.class_level_id && levelMap[b.class_level_id] ? levelMap[b.class_level_id] : 'All levels'}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <span className={`px-2 py-0.5 rounded-full font-bold ${b.mine ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-slate-100 text-slate-600'}`}>
                      {b.mine ? 'Mine' : 'School'}
                    </span>
                    <span className="text-slate-500">
                      <FileText className="w-3 h-3 inline mr-1" />
                      {b.questions ?? 0} questions
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {openBank && (
        questionsQ.isLoading ? (
          <p className="text-sm text-slate-500 py-8 text-center">Loading questions…</p>
        ) : questions.length === 0 ? (
          <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="font-semibold text-slate-700">No questions yet</p>
            <p className="text-sm text-slate-500 mt-1">Click "Add question" to create the first one.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <ul className="divide-y divide-slate-100">
              {questions.map((q: any, idx: number) => (
                <li key={q.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900 whitespace-pre-wrap">{q.body}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                        <span className="px-2 py-0.5 rounded font-bold bg-slate-100 text-slate-700 uppercase">
                          {q.type?.replace('_', ' ')}
                        </span>
                        <span className="text-slate-500">{q.marks ?? 1} mark(s)</span>
                        <span className="text-slate-500 capitalize">{q.difficulty ?? 'easy'}</span>
                      </div>
                      {needsOptions(q.type as QType) && Array.isArray(q.options) && q.options.length > 0 && (
                        <ul className="mt-2 space-y-1 text-xs">
                          {q.options.map((o: any) => (
                            <li key={o.id ?? o.body} className={`flex items-start gap-2 ${o.is_correct ? 'text-green-700 font-semibold' : 'text-slate-600'}`}>
                              <span className="w-5 font-mono">{o.label}</span>
                              <span>{o.body}</span>
                              {o.is_correct && <Check className="w-3 h-3 mt-0.5" />}
                            </li>
                          ))}
                        </ul>
                      )}
                      {q.explanation && (
                        <p className="mt-2 text-xs text-slate-500 italic">Explanation: {q.explanation}</p>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => setQuestionForm({
                        id: q.id,
                        type: q.type,
                        body: q.body,
                        marks: String(q.marks ?? 1),
                        difficulty: q.difficulty ?? 'easy',
                        explanation: q.explanation ?? '',
                        options: (q.options ?? []).map((o: any) => ({ body: o.body, is_correct: !!o.is_correct })),
                      })}
                        className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={async () => { if (await askConfirm('Delete this question?')) deleteQ.mutate(q.id); }}
                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )
      )}

      {/* ─── NEW BANK MODAL ─── */}
      {showNewBank && (
        <NewBankModal
          subjects={subjects}
          levels={levels}
          onClose={() => setShowNewBank(false)}
          onSave={(body) => createBankM.mutate(body)}
          saving={createBankM.isPending}
        />
      )}

      {/* ─── QUESTION EDITOR MODAL ─── */}
      {questionForm && openBank && (
        <QuestionEditorModal
          bankId={openBank.id}
          form={questionForm}
          onClose={() => setQuestionForm(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['question-bank-questions', openBank.id] });
            setQuestionForm(null);
          }}
        />
      )}

      {/* ─── IMPORT MODAL ─── */}
      {showImport && openBank && (
        <ImportModal
          bankId={openBank.id}
          onClose={() => setShowImport(false)}
          onImported={() => {
            qc.invalidateQueries({ queryKey: ['question-bank-questions', openBank.id] });
            setShowImport(false);
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  NEW BANK MODAL
// ═══════════════════════════════════════════════════════════════
function NewBankModal({ subjects, levels, onClose, onSave, saving }: {
  subjects: any[]; levels: any[]; onClose: () => void;
  onSave: (body: any) => void; saving: boolean;
}) {
  const [name, setName] = useState('');
  const [subjectId, setSubjectId] = useState<number | ''>('');
  const [levelId, setLevelId] = useState<number | ''>('');
  const [visibility, setVisibility] = useState<'private' | 'school'>('private');
  const [error, setError] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) { setError('Name is required.'); return; }
    onSave({
      name: name.trim(),
      subject_id: subjectId || null,
      class_level_id: levelId || null,
      visibility,
    });
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white w-full max-w-md rounded-2xl shadow-xl">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
          <h3 className="font-bold text-slate-900">New question bank</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required
              placeholder="e.g. JSS 2 Mathematics – Algebra"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Subject</label>
              <select value={subjectId} onChange={(e) => setSubjectId(e.target.value ? Number(e.target.value) : '')}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">—</option>
                {subjects.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Class level</label>
              <select value={levelId} onChange={(e) => setLevelId(e.target.value ? Number(e.target.value) : '')}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">All levels</option>
                {levels.map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Visibility</label>
            <div className="flex gap-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={visibility === 'private'} onChange={() => setVisibility('private')} />
                Private (only me)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={visibility === 'school'} onChange={() => setVisibility('school')} />
                School-wide
              </label>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
          <button type="button" onClick={onClose}
            className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">Cancel</button>
          <button type="submit" disabled={saving}
            className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
            {saving ? 'Creating…' : 'Create bank'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  QUESTION EDITOR MODAL
// ═══════════════════════════════════════════════════════════════
function QuestionEditorModal({ bankId, form: initial, onClose, onSaved }: {
  bankId: number; form: QuestionForm; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<QuestionForm>(initial);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const createM = useMutation({
    mutationFn: (body: any) => postQuestionBanksQuestions(bankId, body),
    onSuccess: onSaved,
    onError: (e: any) => {
      const msg = e?.response?.data?.message
        || (e?.response?.data?.errors && Object.values(e.response.data.errors).flat().join(' · '))
        || 'Save failed';
      setError(String(msg));
    },
  });

  const updateM = useMutation({
    mutationFn: ({ id, body }: { id: number; body: any }) => putQuestions(id, body),
    onSuccess: onSaved,
    onError: (e: any) => {
      const msg = e?.response?.data?.message
        || (e?.response?.data?.errors && Object.values(e.response.data.errors).flat().join(' · '))
        || 'Update failed';
      setError(String(msg));
    },
  });

  const isEdit = !!form.id;

  const setType = (t: QType) => {
    let opts = form.options;
    if (t === 'true_false') {
      opts = [{ body: 'True', is_correct: true }, { body: 'False', is_correct: false }];
    } else if (t === 'mcq' || t === 'multi') {
      if (opts.length < 2) opts = [{ body: '', is_correct: true }, { body: '', is_correct: false }, { body: '', is_correct: false }, { body: '', is_correct: false }];
    } else {
      opts = [];
    }
    setForm({ ...form, type: t, options: opts });
  };

  const toggleCorrect = (idx: number) => {
    if (form.type === 'multi') {
      setForm({ ...form, options: form.options.map((o, i) => i === idx ? { ...o, is_correct: !o.is_correct } : o) });
    } else {
      setForm({ ...form, options: form.options.map((o, i) => ({ ...o, is_correct: i === idx })) });
    }
  };

  const updateOptBody = (idx: number, body: string) => {
    setForm({ ...form, options: form.options.map((o, i) => i === idx ? { ...o, body } : o) });
  };

  const addOption = () => {
    if (form.options.length >= 8) return;
    setForm({ ...form, options: [...form.options, { body: '', is_correct: false }] });
  };

  const removeOption = (idx: number) => {
    if (form.options.length <= 2) return;
    setForm({ ...form, options: form.options.filter((_, i) => i !== idx) });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.body.trim()) { setError('Question body is required.'); return; }
    if (needsOptions(form.type)) {
      const filled = form.options.filter(o => o.body.trim());
      if (filled.length < 2) { setError('At least 2 options need text.'); return; }
      if (!filled.some(o => o.is_correct)) { setError('Mark at least one option as correct.'); return; }
      if (form.type !== 'multi' && filled.filter(o => o.is_correct).length !== 1) {
        setError('Exactly one option must be correct (use "multiple" type for multi-answer).');
        return;
      }
    }
    const body: any = {
      type: form.type,
      body: form.body.trim(),
      marks: Number(form.marks) || 1,
      difficulty: form.difficulty,
      explanation: form.explanation.trim() || null,
    };
    if (needsOptions(form.type)) {
      body.options = form.options.filter(o => o.body.trim()).map(o => ({
        body: o.body.trim(),
        is_correct: o.is_correct,
      }));
    }
    if (isEdit) {
      updateM.mutate({ id: form.id!, body });
    } else {
      createM.mutate(body);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <form onSubmit={submit} className="bg-white w-full max-w-2xl rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <h3 className="font-bold text-slate-900">{isEdit ? 'Edit question' : 'Add question'}</h3>
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Type *</label>
                <select value={form.type} onChange={(e) => setType(e.target.value as QType)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="mcq">Multiple choice (one correct)</option>
                  <option value="multi">Multiple select (many correct)</option>
                  <option value="true_false">True / False</option>
                  <option value="short">Short answer</option>
                  <option value="essay">Essay</option>
                  <option value="fill_blank">Fill in the blank</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Difficulty</label>
                <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value as Difficulty })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Marks</label>
                <input type="number" min={0.5} step={0.5} value={form.marks}
                  onChange={(e) => setForm({ ...form, marks: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Question *</label>
              <textarea rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                required placeholder="Type the question here…"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>

            {needsOptions(form.type) && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-slate-700">
                    Options * <span className="text-xs font-normal text-slate-500 ml-1">
                      (click the circle/tick to mark correct)
                    </span>
                  </label>
                  {form.type !== 'true_false' && form.options.length < 8 && (
                    <button type="button" onClick={addOption}
                      className="text-xs font-semibold text-[#0f172a] hover:underline inline-flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Add option
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {form.options.map((o, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <button type="button" onClick={() => toggleCorrect(idx)}
                        className={`w-6 h-6 rounded-full flex-shrink-0 border-2 flex items-center justify-center mt-1 ${
                          o.is_correct ? 'bg-green-600 border-green-600' : 'bg-white border-slate-300'
                        }`}>
                        {o.is_correct && <Check className="w-3 h-3 text-white" />}
                      </button>
                      <span className="w-6 mt-2 text-xs font-mono text-slate-500">
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <input type="text" value={o.body} onChange={(e) => updateOptBody(idx, e.target.value)}
                        disabled={form.type === 'true_false'}
                        placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                        className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-50" />
                      {form.type !== 'true_false' && form.options.length > 2 && (
                        <button type="button" onClick={() => removeOption(idx)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded mt-0.5">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Explanation (optional)</label>
              <textarea rows={2} value={form.explanation}
                onChange={(e) => setForm({ ...form, explanation: e.target.value })}
                placeholder="Shown to students after the exam, or to teachers when reviewing"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">Cancel</button>
            <button type="submit" disabled={createM.isPending || updateM.isPending}
              className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
              {createM.isPending || updateM.isPending ? 'Saving…' : (isEdit ? 'Save changes' : 'Add question')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  IMPORT MODAL
// ═══════════════════════════════════════════════════════════════
function ImportModal({ bankId, onClose, onImported }: {
  bankId: number; onClose: () => void; onImported: () => void;
}) {
  const [json, setJson] = useState('');
  const [error, setError] = useState<string | null>(null);

  const importM = useMutation({
    mutationFn: (questions: any[]) => postQuestionBanksImport(bankId, { questions }),
    onSuccess: (r: any) => {
      const n = r?.data?.data?.imported ?? r?.data?.imported ?? 0;
      alert(`✅ ${n} question(s) imported`);
      onImported();
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message
        || (e?.response?.data?.errors && Object.values(e.response.data.errors).flat().join('\n'))
        || 'Import failed';
      setError(String(msg));
    },
  });

  const submit = () => {
    setError(null);
    try {
      const parsed = JSON.parse(json);
      const questions = Array.isArray(parsed) ? parsed : parsed?.questions;
      if (!Array.isArray(questions) || questions.length === 0) {
        setError('Expected a non-empty array of questions.'); return;
      }
      importM.mutate(questions);
    } catch (e: any) {
      setError('Invalid JSON: ' + e.message);
    }
  };

  const sample = `[
  {
    "type": "mcq",
    "body": "What is 2 + 2?",
    "marks": 1,
    "difficulty": "easy",
    "options": [
      { "body": "3", "is_correct": false },
      { "body": "4", "is_correct": true },
      { "body": "5", "is_correct": false },
      { "body": "6", "is_correct": false }
    ]
  },
  {
    "type": "short",
    "body": "Define photosynthesis.",
    "marks": 2,
    "difficulty": "medium"
  }
]`;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <h3 className="font-bold text-slate-900">Import questions (JSON)</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 whitespace-pre-wrap">
                {error}
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-semibold text-slate-700">Paste question array</label>
                <button onClick={() => setJson(sample)}
                  className="text-xs font-semibold text-[#0f172a] hover:underline inline-flex items-center gap-1">
                  <Clipboard className="w-3 h-3" /> Load sample
                </button>
              </div>
              <textarea rows={14} value={json} onChange={(e) => setJson(e.target.value)}
                placeholder={sample}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono" />
              <p className="text-xs text-slate-500 mt-1">
                Structure matches the manual "Add question" form. Same field names.
              </p>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
            <button onClick={onClose}
              className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">Cancel</button>
            <button onClick={submit} disabled={importM.isPending || !json.trim()}
              className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
              {importM.isPending ? 'Importing…' : 'Import'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
